/**
 * Modal de gestion des quotas annuels de congés par le secrétariat.
 *
 * Fonctionnalités :
 *   - Liste de tous les quotas existants (groupés par employé)
 *   - Ajout d'un quota : employee + type + période + jours
 *   - Édition inline des champs
 *   - Suppression
 *   - La période est libre (année civile ou année de référence juin-mai
 *     selon les besoins)
 */
import { useState, useMemo } from 'react';
import { X, Plus, Trash2, Edit2, Save } from 'lucide-react';
import { useLeaveQuotas, type QuotaInput } from '../hooks/useLeaveQuotas';
import type { QuotaRecord } from '../utils/leaveBalanceUtils';
import { DISPLAYED_LEAVE_TYPES, LEAVE_TYPE_LABELS } from '../utils/leaveBalanceUtils';

interface Employee {
  id: string;
  full_name: string;
}

interface QuotasModalProps {
  isOpen: boolean;
  onClose: () => void;
  employees: Employee[];
}

export function QuotasModal({ isOpen, onClose, employees }: QuotasModalProps) {
  const { quotas, createQuota, updateQuota, deleteQuota } = useLeaveQuotas();

  const [filterEmployee, setFilterEmployee] = useState<string>('all');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);

  const empMap = useMemo(
    () => Object.fromEntries(employees.map(e => [e.id, e.full_name])),
    [employees],
  );

  const filtered = useMemo(() =>
    filterEmployee === 'all'
      ? quotas
      : quotas.filter(q => q.employee_id === filterEmployee),
    [quotas, filterEmployee],
  );

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(4px)',
      zIndex: 1000, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: '40px 20px',
      overflowY: 'auto',
    }}>
      <div style={{
        background: 'white', borderRadius: '20px', width: '100%', maxWidth: '900px',
        display: 'flex', flexDirection: 'column', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
      }}>

        {/* Header */}
        <div style={{
          padding: '24px 32px', borderBottom: '1px solid #e2e8f0',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.4rem', color: '#0f172a', fontWeight: 700 }}>
              Quotas de congés par employé
            </h2>
            <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: '#64748b' }}>
              Définissez les jours alloués par employé, type et période. La consommation est calculée automatiquement depuis les congés validés.
            </p>
          </div>
          <button onClick={onClose} style={{
            background: 'transparent', border: 'none', cursor: 'pointer', padding: '8px',
            borderRadius: '8px', color: '#64748b',
          }}>
            <X size={22} />
          </button>
        </div>

        {/* Toolbar */}
        <div style={{
          padding: '16px 32px', borderBottom: '1px solid #f1f5f9',
          display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap',
        }}>
          <select
            value={filterEmployee}
            onChange={e => setFilterEmployee(e.target.value)}
            style={{
              padding: '8px 12px', borderRadius: '10px', border: '1px solid #e2e8f0',
              background: 'white', fontSize: '0.9rem', minWidth: '220px',
            }}>
            <option value="all">Tous les employés</option>
            {employees.map(e => <option key={e.id} value={e.id}>{e.full_name}</option>)}
          </select>
          <button onClick={() => setShowAddForm(!showAddForm)} style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            padding: '9px 16px', background: '#7c3aed', color: 'white',
            border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem',
          }}>
            <Plus size={16} /> Nouveau quota
          </button>
          <div style={{ marginLeft: 'auto', fontSize: '0.8rem', color: '#94a3b8' }}>
            {filtered.length} quota{filtered.length > 1 ? 's' : ''}
          </div>
        </div>

        {/* Add form */}
        {showAddForm && (
          <AddQuotaForm
            employees={employees}
            defaultEmployeeId={filterEmployee !== 'all' ? filterEmployee : undefined}
            onCancel={() => setShowAddForm(false)}
            onSubmit={async (input) => {
              const ok = await createQuota(input);
              if (ok) setShowAddForm(false);
            }}
          />
        )}

        {/* List */}
        <div style={{ padding: '8px 32px 24px', maxHeight: '60vh', overflowY: 'auto' }}>
          {filtered.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
              Aucun quota défini. Cliquez sur « Nouveau quota » pour commencer.
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0 6px', fontSize: '0.88rem' }}>
              <thead>
                <tr style={{ color: '#64748b', textTransform: 'uppercase', fontSize: '0.72rem', letterSpacing: '0.05em' }}>
                  <th style={{ textAlign: 'left', padding: '6px 8px' }}>Employé</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px' }}>Type</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px' }}>Période</th>
                  <th style={{ textAlign: 'right', padding: '6px 8px' }}>Jours</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px' }}>Notes</th>
                  <th style={{ width: '80px' }}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(q => (
                  <QuotaRow
                    key={q.id}
                    quota={q}
                    employeeName={empMap[q.employee_id] ?? '??'}
                    editing={editingId === q.id}
                    onStartEdit={() => setEditingId(q.id)}
                    onCancelEdit={() => setEditingId(null)}
                    onSave={async (patch) => {
                      const ok = await updateQuota(q.id, patch);
                      if (ok) setEditingId(null);
                    }}
                    onDelete={() => deleteQuota(q.id)}
                  />
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Form d'ajout
// ============================================================================

function AddQuotaForm({
  employees, defaultEmployeeId, onCancel, onSubmit,
}: {
  employees: Employee[];
  defaultEmployeeId?: string;
  onCancel: () => void;
  onSubmit: (input: QuotaInput) => Promise<void>;
}) {
  const currentYear = new Date().getFullYear();
  const [input, setInput] = useState<QuotaInput>({
    employee_id: defaultEmployeeId ?? employees[0]?.id ?? '',
    type: 'cp',
    period_start: `${currentYear}-01-01`,
    period_end: `${currentYear}-12-31`,
    total_days: 25,
    notes: '',
  });

  return (
    <div style={{
      background: '#f8fafc', margin: '0 32px 8px', padding: '16px', borderRadius: '12px',
      border: '1px solid #e2e8f0', display: 'grid', gridTemplateColumns: 'repeat(2, 1fr) auto', gap: '12px', alignItems: 'end',
    }}>
      <Field label="Employé">
        <select value={input.employee_id} onChange={e => setInput({ ...input, employee_id: e.target.value })} style={inputStyle}>
          {employees.map(e => <option key={e.id} value={e.id}>{e.full_name}</option>)}
        </select>
      </Field>
      <Field label="Type">
        <select value={input.type} onChange={e => setInput({ ...input, type: e.target.value })} style={inputStyle}>
          {DISPLAYED_LEAVE_TYPES.map(t => <option key={t} value={t}>{LEAVE_TYPE_LABELS[t]}</option>)}
        </select>
      </Field>
      <Field label="Début">
        <input type="date" value={input.period_start} onChange={e => setInput({ ...input, period_start: e.target.value })} style={inputStyle} />
      </Field>
      <Field label="Fin">
        <input type="date" value={input.period_end} onChange={e => setInput({ ...input, period_end: e.target.value })} style={inputStyle} />
      </Field>
      <Field label="Jours alloués">
        <input type="number" step="0.5" min="0" value={input.total_days}
          onChange={e => setInput({ ...input, total_days: parseFloat(e.target.value) || 0 })} style={inputStyle} />
      </Field>
      <Field label="Notes (optionnel)">
        <input type="text" value={input.notes ?? ''} placeholder="Ex: reliquat 2025"
          onChange={e => setInput({ ...input, notes: e.target.value })} style={inputStyle} />
      </Field>
      <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
        <button onClick={onCancel} style={btnSecondary}>Annuler</button>
        <button onClick={() => onSubmit(input)} disabled={!input.employee_id} style={btnPrimary}>Créer</button>
      </div>
    </div>
  );
}

// ============================================================================
// Row éditable
// ============================================================================

function QuotaRow({
  quota, employeeName, editing, onStartEdit, onCancelEdit, onSave, onDelete,
}: {
  quota: QuotaRecord;
  employeeName: string;
  editing: boolean;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSave: (patch: Partial<QuotaInput>) => Promise<void>;
  onDelete: () => void;
}) {
  const [totalDays, setTotalDays] = useState(quota.total_days);
  const [periodStart, setPeriodStart] = useState(quota.period_start);
  const [periodEnd, setPeriodEnd] = useState(quota.period_end);
  const [notes, setNotes] = useState(quota.notes ?? '');

  const cellStyle: React.CSSProperties = {
    padding: '10px 8px', background: 'white', borderTop: '1px solid #f1f5f9', borderBottom: '1px solid #f1f5f9',
  };
  const firstCell: React.CSSProperties = { ...cellStyle, borderLeft: '1px solid #f1f5f9', borderTopLeftRadius: '8px', borderBottomLeftRadius: '8px' };
  const lastCell: React.CSSProperties = { ...cellStyle, borderRight: '1px solid #f1f5f9', borderTopRightRadius: '8px', borderBottomRightRadius: '8px', textAlign: 'right' };

  return (
    <tr>
      <td style={firstCell}><strong>{employeeName}</strong></td>
      <td style={cellStyle}>{LEAVE_TYPE_LABELS[quota.type] ?? quota.type}</td>
      <td style={cellStyle}>
        {editing ? (
          <div style={{ display: 'flex', gap: '4px' }}>
            <input type="date" value={periodStart} onChange={e => setPeriodStart(e.target.value)} style={{ ...inputStyle, padding: '4px 6px', width: '130px', fontSize: '0.8rem' }} />
            <input type="date" value={periodEnd} onChange={e => setPeriodEnd(e.target.value)} style={{ ...inputStyle, padding: '4px 6px', width: '130px', fontSize: '0.8rem' }} />
          </div>
        ) : `${formatShort(quota.period_start)} → ${formatShort(quota.period_end)}`}
      </td>
      <td style={{ ...cellStyle, textAlign: 'right', fontWeight: 700 }}>
        {editing ? (
          <input type="number" step="0.5" min="0" value={totalDays} onChange={e => setTotalDays(parseFloat(e.target.value) || 0)}
            style={{ ...inputStyle, padding: '4px 6px', width: '80px', fontSize: '0.85rem', textAlign: 'right' }} />
        ) : `${quota.total_days} j`}
      </td>
      <td style={cellStyle}>
        {editing ? (
          <input type="text" value={notes} onChange={e => setNotes(e.target.value)} style={{ ...inputStyle, padding: '4px 6px', fontSize: '0.8rem' }} placeholder="Notes" />
        ) : (quota.notes || <span style={{ color: '#cbd5e1' }}>—</span>)}
      </td>
      <td style={lastCell}>
        {editing ? (
          <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
            <button onClick={() => onSave({ total_days: totalDays, period_start: periodStart, period_end: periodEnd, notes })}
              style={{ ...iconBtn, color: '#16a34a' }} title="Enregistrer"><Save size={16} /></button>
            <button onClick={onCancelEdit} style={iconBtn} title="Annuler"><X size={16} /></button>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
            <button onClick={onStartEdit} style={iconBtn} title="Modifier"><Edit2 size={14} /></button>
            <button onClick={onDelete} style={{ ...iconBtn, color: '#dc2626' }} title="Supprimer"><Trash2 size={14} /></button>
          </div>
        )}
      </td>
    </tr>
  );
}

// ============================================================================
// Styles utilitaires
// ============================================================================

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</label>
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: '8px',
  fontSize: '0.85rem', background: 'white', color: '#1e293b',
};

const btnPrimary: React.CSSProperties = {
  padding: '8px 18px', background: '#7c3aed', color: 'white', border: 'none', borderRadius: '10px',
  cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem',
};

const btnSecondary: React.CSSProperties = {
  padding: '8px 18px', background: 'white', color: '#475569', border: '1px solid #e2e8f0',
  borderRadius: '10px', cursor: 'pointer', fontWeight: 500, fontSize: '0.85rem',
};

const iconBtn: React.CSSProperties = {
  padding: '6px', background: 'transparent', border: 'none', cursor: 'pointer', color: '#64748b', borderRadius: '6px',
};

function formatShort(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y.slice(2)}`;
}
